/**
 * LanguageSelector Component Documentation
 *
 * This component provides language selection functionality for the Silanga application.
 * It allows users to switch between different language options, currently supporting:
 * - English
 * - Inuktitut (Syllabics)
 *
 * Features:
 * 1. Responsive design that works on both mobile and desktop
 * 2. Visual feedback for the currently selected language
 * 3. Simple and intuitive interface
 * 4. Maintains language state through the application
 *
 * Usage:
 * This component should be placed in the navigation bar and requires
 * currentLanguage and setCurrentLanguage props to function properly.
 */

import React, { useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";

/**
 * LanguageSelector Component
 *
 * @param {Object} props - Component props
 * @param {string} props.currentLanguage - Currently selected language code
 * @param {Function} props.setCurrentLanguage - Function to update the selected language
 * @returns {JSX.Element} The rendered language selector component
 */
const LanguageSelector = ({ currentLanguage, setCurrentLanguage }) => {
  // State for managing the language selection menu
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  /**
   * Available languages configuration
   * Each language has a code and display label
   * Note: Roman Inuktitut is currently commented out
   */
  const languages = [
    { code: "english", label: "English" },
    { code: "syllabics", label: "ᐃᓄᒃᑎᑐᑦ" },
    // { code: 'roman', label: 'Inuktitut' }
  ];

  /**
   * Handles the click event on the language selector
   * Opens the language selection menu
   */
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Closes the language selection menu
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Handles language selection
   * Updates the current language and closes the menu
   *
   * @param {string} languageCode - The code of the selected language
   */
  const handleLanguageSelect = (languageCode) => {
    setCurrentLanguage(languageCode);
    handleClose();
  };

  return (
    <Box sx={{ display: "flex", gap: "8px" }}>
      {languages.map((language) => (
        <Button
          key={language.code}
          onClick={() => setCurrentLanguage(language.code)}
          sx={{
            minWidth: "auto",
            padding: "4px 12px",
            backgroundColor:
              currentLanguage === language.code ? "white" : "transparent",
            color: currentLanguage === language.code ? "black" : "white",
            borderRadius: "20px",
            "&:hover": {
              backgroundColor:
                currentLanguage === language.code
                  ? "white"
                  : "rgba(255, 255, 255, 0.1)",
            },
            textTransform: "none",
            fontWeight: 500,
            fontSize: "14px",
            boxShadow: "none",
            border: "none",
          }}
        >
          {language.label}
        </Button>
      ))}
    </Box>
  );
};

export default LanguageSelector;
